import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import { CategoryModel } from '../../siteadmin/models/CategoryModel';
import Config from '../../shared/config';
import { LearnerCategorySchema } from '../models/LearnerCategorySchema';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

interface argType {
  categorykey: number;
}

export interface CategoryItemKey {
  itemkey: number;
}

/** Usergroup with minimal properties.
 * We require `autojoin` only.
 */
interface UserGroup {
  groupkey: number;
  domainkey: number;
  title: string;
  autojoin: boolean;
  nodelete: boolean;
  public: boolean;
}

export const useLearnerCategories = () => {
  const userkey = AuthService.Userkey;
  //API endpoints to get category list
  const listCategoriesUrl = `${Config.API}/api/category/list/category/domain`;
  const listCategorySchemaUrl = `${Config.API}/api/category/list/objects`;
  const listUserGroupsUrl = `${Config.API}/api/usergroup/list/group/user/${userkey}`;
  const saveUserSelectionUrl = `${Config.API}/api/category/action/user/${userkey}`;

  const [learnerCategorySchemas, setLearnerCategorySchemas] =
    useState<LearnerCategorySchema[]>();

  const headers = {
    Authorization: `Bearer ${AuthService.getAccessToken()}`,
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };

  const fetchCategorySchema = async (
    url: string,
    { arg }: { arg: argType }
  ) => {
    return fetch(`${url}/${arg.categorykey}`, {
      headers,
      method: 'GET'
    }).then((res) => res.json());
  };

  // Get Category schema.
  const { trigger: getCategorySchema, isMutating: schemaLoading } =
    useSWRMutation(listCategorySchemaUrl, fetchCategorySchema);

  //List categories.
  const {
    data: categories,
    error: loadError,
    isLoading: loadingCategories
  } = useSWR<AxiosResponse<CategoryModel[]>, AxiosError<Error>>(
    listCategoriesUrl
  );

  //List user groups.
  const {
    mutate: loadUserGroups,
    data: userGroups,
    error: loadUserGroupsError,
    isLoading: userGroupsLoading
  } = useSWR<AxiosResponse<UserGroup[]>, AxiosError<Error>>(listUserGroupsUrl, {
    revalidateOnMount: false
  });

  //Save user selection items
  const saveSelectionRequest = async (
    url: string,
    { arg }: { arg: CategoryItemKey[] }
  ) => {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(arg)
    });
    // SWR doesn't automatically throw errors for HTTP status codes like 401 (Unauthorized).
    // So we need to manually check for error status
    if (!response.ok) {
      throw new Error('Failed to save user selection');
    }
    return response;
  };

  const {
    trigger: saveSelection,
    isMutating: saving,
    error: saveError
  } = useSWRMutation(saveUserSelectionUrl, saveSelectionRequest);

  useEffect(() => {
    if (categories?.data?.length) {
      Promise.all(
        categories.data.map(async (c) => {
          const categorySchema = await getCategorySchema({
            categorykey: c.categorykey
          });
          return categorySchema;
        })
      ).then((schemas) => {
        setLearnerCategorySchemas(schemas);
      });
    } else {
      setLearnerCategorySchemas([]);
    }
  }, [categories]);

  return {
    allCategories: learnerCategorySchemas,
    loadingCategories: loadingCategories || schemaLoading,
    loadError,
    saveSelection,
    saving,
    saveError,
    loadUserGroups,
    userGroupsLoading,
    userGroups: userGroups?.data,
    loadUserGroupsError
  };
};
